<template>
  <div class="container">
    <h2 class="mb-5">Manage users</h2>

    <div class="row">
      <div class="col-12">
        <r-table
          :columns="columns"
          :items="users"
          @onSort="handleOnSort"
          @onItemClick="handleOnItemClick"
        />
      </div>
    </div>
    <div class="row">
      <div class="col-4">
        <base-select label="Items" v-model="itemsPerPage" :options="itemsPerPageOptions"/>
      </div>
      <div class="col-8 text-right">
        <r-pagination
          :totalPages="totalPages"
          :total="total"
          :currentPage="currentPage"
          @pageChanged="handlePageChanged"
        />
      </div>
    </div>
    <modal :name="modalName" height="auto" style="z-index:9999" :adaptive="true" width="80%" :scrollable="true">
      <div class="modal-header">
        <h5 class="modal-title">{{currentUser.name}}</h5>
      </div>
      <div class="modal-body">
        <div class="row">
          <div class="col-12">
            <form @submit.prevent="onSubmit">
              <base-text
                name="username"
                v-validate="'required'"
                label="Username"
                v-model="currentUser.name"
              />
              <base-text
                name="password"
                type="password"
                v-validate="'min:6'"
                label="Password"
                v-model="currentUser.password"
                :err="errors.collect('password')"
              />
              <button class="btn btn-primary" type="submit">Update</button>
            </form>
            <hr>
          </div>

          <div class="col-12">
            <h5>Change user status:</h5>
            <button
              class="btn btn-link pl-0"
              @click="toggleUserAccess(currentUser.blocked, currentUser)"
            >{{currentUser.blocked ? 'Unblock user' : 'Block user'}}</button>
            <hr>
          </div>

          <div class="col-12" v-if="!currentUser.accepted">
            <h5>User not accepted after registration:</h5>
            <button
              class="btn btn-link pl-0"
              @click="adminEditAcceptUser(currentUser.id); currentUser.accepted = true"
            >Accepted</button>
            <hr>
          </div>
          <div class="col-12" v-if="currentUser.login_attempts">
            <h5>Unblock user login:</h5>
            <button
              class="btn btn-link pl-0"
              @click="adminEditUnblockLoginUser(currentUser.id); currentUser.login_attempts = null"
            >Unblock user login</button>
            <hr>
          </div>

          <div class="col-12">
            <div v-if="true">
              <h6>Select user company:</h6>
              <companies-list-table
                :selectedCompaniesId="selectedCompaniesId"
                :companies="companies"
                @onSelectedCompaniesIdChange="handleOnSelectedCompaniesIdChange"
                :displayItems="5"
              />
              <button
                class="btn btn-primary"
                type="button"
                @click="onAttachCompanies"
              >Attach companies</button>
            </div>
          </div>
        </div>
      </div>
      <div class="modal-footer">
        <base-button radius="bl" component-class="blue" @click.native="$modal.hide(modalName)">Close</base-button>
      </div>
    </modal>
    <div class="row">
      <div class="col-6">
        <base-button radius="bl" component-class="blue" @click.native="$router.push({ name: 'user.dashboard' })">
          <i class="fas fa-arrow-left mr-3"></i> Back
        </base-button>
      </div>
      <div class="col-6 text-right">
        <base-button
          radius="bl"
          component-class="green"
          @click.native="downloadUsers"
        >Get users list in CSV format</base-button>
      </div>
    </div>
  </div>
</template>

<script>
    import { mapState, mapActions } from 'vuex';
    import moment from 'moment';
    import _orderBy from 'lodash/orderBy';
    import BaseText from '@/components/inputs/text/BaseText';
    import BaseButton from '@/components/inputs/button/BaseButton';
    import BaseSelect from '@/components/inputs/select/BaseSelect';
    import Pagination from '@/components/Pagination/Pagination';
    import Table from '@/components/Table/Table';
    import CompaniesListTable from '@/components/Table/CompaniesListTable/CompaniesListTable';
    import HTTP from '@/services/HTTP';

    export default {
        data() {
            return {
                modalName: 'manageUsers',
                itemsPerPage: 5,
                itemsPerPageOptions: [5, 15, 30, 50],
                currentPage: 1,
                selectedCompaniesId: [],
                columns: [
                    {
                        name: 'Username',
                        field: 'name',
                        sortable: true,
                        options: {
                            type: 'button'
                        }
                    },
                    {
                        name: 'Email',
                        field: 'email'
                    },
                    {
                        name: 'Blocked at',
                        field: 'blocked'
                    },
                    {
                        name: 'Accepted at',
                        field: 'accepted'
                    },
                    {
                        name: 'Login attempt',
                        field: 'loginAttempt'
                    }
            ],
                total: 0,
                data: [],
                currentUser: {},
                order: 'asc'
            };
        },

        computed: {
            ...mapState({
                allUsers: state => state.user.users,
                companies: state => state.userCompanies.data
            }),
            totalPages() {
                return Math.ceil(this.total / this.itemsPerPage);
            },
            users() {
                const skipUsers =
                    this.currentPage === 1 ? 0 : (this.currentPage - 1) * this.itemsPerPage;
                return this.allUsers
                    .map(user => {
                        if (user.login_attempts) {
                            const { attempt } = JSON.parse(user.login_attempts);
                            user.loginAttempt = attempt;
                        }

                        return user;
                    })
                    .slice(skipUsers, skipUsers + this.itemsPerPage);
            }
        },

        watch: {
            itemsPerPage() {
                this.currentPage = 1;
            },
            allUsers(newValue) {
                this.data = newValue;
                this.total = newValue.length;
            }
        },

        methods: {
            ...mapActions({
                getUsersList: 'user/getUsersList',
                adminEditUserBaseData: 'user/adminEditUserBaseData',
                adminEditAcceptUser: 'user/adminEditAcceptUser',
                adminEditUnblockLoginUser: 'user/adminEditUnblockLoginUser',
                adminEditUserAttachCompany: 'user/adminEditUserAttachCompany',
                getCompanies: 'userCompanies/getCompaniesWithFirstUser'
            }),
            handlePageChanged(page) {
                this.currentPage = page;
            },
            handleOnSort(field) {
                this.order = this.order === 'asc' ? 'desc' : 'asc';
                this.data = _orderBy(this.data, field, this.order);
            },
            handleOnSelectedCompaniesIdChange(value) {
              this.selectedCompaniesId = value;
            },
            onSubmit() {
                const { id, name, password } = this.currentUser;
                const updateUserData = { id, name };
                const isPasswordValid = this.$validator.flags.password.valid;

                if (password && isPasswordValid) {
                    updateUserData.password = password;
                }
                this.adminEditUserBaseData(updateUserData);
            },
            onAttachCompanies() {
                const attachmentData = {
                    userId: this.currentUser.id,
                    company_ids: this.selectedCompaniesId
                };

                this.adminEditUserAttachCompany(attachmentData);
            },
            async toggleUserAccess(isBlocked, user) {
                const userAccessUrl = isBlocked
                    ? `/user/${user.id}/unblock-account`
                    : `user/${user.id}/block`;
                const toggleUserAccess = isBlocked ? null : moment().format();
                this.currentUser = user;

                try {
                    const { data } = await HTTP.put(userAccessUrl);
                    if (data && data.status === 1) {
                        this.currentUser.blocked = toggleUserAccess;
                    }
                } catch (error) {
                    console.error(error.message);
                }
            },
            async getUserAttachedCompanies(userId) {
                try {
                    const { data } = await HTTP.get(`user/${userId}/companies`);
                    this.selectedCompaniesId = data.companies;
                } catch (error) {
                    console.error(error);
                }
            },
            async handleOnItemClick({ field, item }) {
                await this.getUserAttachedCompanies(item.id);

                if (field === 'name') {
                    this.$modal.show(this.modalName);
                    this.currentUser = item;
                }
            },
            async downloadUsers() {
                try {
                    const { data } = await HTTP.get('user/export');
                    const url = window.URL.createObjectURL(new Blob([data]));
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', 'file.csv'); //or any other extension
                    document.body.appendChild(link);
                    link.click();
                } catch (error) {
                    this.$snotify.error(error.message);
                }
            },
        },

        mounted() {
            this.getUsersList();
            this.getCompanies();
        },

        components: {
            'base-button': BaseButton,
            'base-select': BaseSelect,
            'base-text': BaseText,
            'r-pagination': Pagination,
            'r-table': Table,
            'companies-list-table': CompaniesListTable,
        }
    };
</script>

<style lang="scss">
.admin-users-companies {
  max-height: 178px;
  overflow: auto;
  li.list-group-item {
    padding: 0.5rem 1rem;
    cursor: pointer;
  }
}
</style>
